import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import GroupsIcon from "@mui/icons-material/Groups";
import LanguageIcon from "@mui/icons-material/Language";
import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import { Box, Button, Chip, Grid, Typography, useMediaQuery } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const serviceImages = {
  germanLanguage: "/service-german-language.png",
  universityAdmission: "/service-university-admission.png",
  ausbildung: "/service-ausbildung.png",
  graduateJobSearch: "/service-graduate-job-search.png",
  corporateTraining: "/service-corporate-language-cultural-training.png",
  corporateRecruitment: "/service-corporate-recruitment.png",
  events: "/service-events.png",
};

const ServicesTile = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith("ar");
  const canHover = useMediaQuery("(hover: hover) and (pointer: fine)");
  const [flippedIndex, setFlippedIndex] = useState(null);
  const navigate = useNavigate();
  const tr = (key, enDefault, arDefault) =>
    t(key, { defaultValue: isArabic ? arDefault : enDefault });
  const tr3 = (key, enDefault, deDefault, arDefault) => {
    const lang = i18n.language?.split("-")[0];
    const fallback = lang === "ar" ? arDefault : lang === "de" ? deDefault : enDefault;
    return t(key, { defaultValue: fallback });
  };
  const serviceSubtitle = t("services.cardSubtitle", {
    defaultValue: isArabic
      ? "\u062e\u062f\u0645\u0627\u062a \u062a\u0639\u0644\u064a\u0645\u064a\u0629 \u0648\u0645\u0647\u0646\u064a\u0629 \u0648\u0644\u063a\u0648\u064a\u0629 \u062a\u0633\u0627\u0639\u062f \u0627\u0644\u0623\u0641\u0631\u0627\u062f \u0648\u0627\u0644\u0634\u0631\u0643\u0627\u062a \u0639\u0644\u0649 \u0628\u0646\u0627\u0621 \u0645\u0633\u0627\u0631 \u0648\u0627\u0636\u062d \u0645\u0639 \u0623\u0644\u0645\u0627\u0646\u064a\u0627."
      : "Focused education, career, language, and hiring services for individuals and companies connected to Germany.",
  });

  const serviceItems = [
    {
      key: "germanLanguage",
      title: t("services.language.title"),
      description: t("services.language.description"),
      button: t("services.language.button"),
      icon: <LanguageIcon fontSize="large" htmlColor="var(--primary-color)" />,
      image: serviceImages.germanLanguage,
      onClick: () => navigate("/german-language"),
    },
    {
      key: "universityAdmission",
      comingSoon: true,
      title: tr("services.university.title", "University Admission", "\u0627\u0644\u0642\u0628\u0648\u0644 \u0627\u0644\u062c\u0627\u0645\u0639\u064a"),
      description: tr(
        "services.university.description",
        "Find the right university, prepare your documents, and submit your application with clear support.",
        "\u0646\u0633\u0627\u0639\u062f\u0643 \u0641\u064a \u0627\u062e\u062a\u064a\u0627\u0631 \u0627\u0644\u062c\u0627\u0645\u0639\u0629 \u0627\u0644\u0645\u0646\u0627\u0633\u0628\u0629\u060c \u062a\u062c\u0647\u064a\u0632 \u0645\u0644\u0641\u0643\u060c \u0648\u0627\u0644\u062a\u0642\u062f\u064a\u0645 \u0628\u062e\u0637\u0648\u0627\u062a \u0648\u0627\u0636\u062d\u0629."
      ),
      button: tr("services.university.button", "Start Applying", "\u0627\u0628\u062f\u0623 \u0627\u0644\u062a\u0642\u062f\u064a\u0645"),
      icon: <SchoolIcon fontSize="large" htmlColor="var(--primary-color)" />,
      image: serviceImages.universityAdmission,
      onClick: () => navigate("/university-admission"),
    },
    {
      key: "ausbildung",
      title: tr("services.ausbildung.title", "Ausbildung", "\u0627\u0644\u062a\u062f\u0631\u064a\u0628 \u0627\u0644\u0645\u0647\u0646\u064a Ausbildung"),
      description: tr(
        "services.ausbildung.description",
        "Support for choosing the right Ausbildung path, preparing your file, and applying with confidence.",
        "\u062f\u0639\u0645 \u0643\u0627\u0645\u0644 \u0644\u0627\u062e\u062a\u064a\u0627\u0631 \u0628\u0631\u0646\u0627\u0645\u062c Ausbildung \u0627\u0644\u0645\u0646\u0627\u0633\u0628 \u0648\u062a\u062c\u0647\u064a\u0632 \u0645\u0644\u0641 \u0627\u0644\u062a\u0642\u062f\u064a\u0645 \u0648\u0627\u0644\u062a\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062c\u0647\u0627\u062a."
      ),
      button: tr("services.ausbildung.button", "Learn More", "\u0627\u0639\u0631\u0641 \u0627\u0644\u0645\u0632\u064a\u062f"),
      icon: <WorkIcon fontSize="large" htmlColor="var(--primary-color)" />,
      image: serviceImages.ausbildung,
      onClick: () => navigate("/ausbildung"),
    },
    {
      key: "graduateJobSearch",
      comingSoon: true,
      title: tr("services.graduateJobSearch.title", "Job Search for Graduates", "\u0627\u0644\u0628\u062d\u062b \u0639\u0646 \u0639\u0645\u0644 (\u0644\u0644\u062e\u0631\u064a\u062c\u064a\u0646)"),
      description: tr(
        "services.graduateJobSearch.description",
        "Help graduates prepare their CV, understand the job market, and apply for suitable opportunities.",
        "\u0645\u0633\u0627\u0639\u062f\u0629 \u0627\u0644\u062e\u0631\u064a\u062c\u064a\u0646 \u0641\u064a \u062a\u062c\u0647\u064a\u0632 \u0627\u0644\u0633\u064a\u0631\u0629 \u0627\u0644\u0630\u0627\u062a\u064a\u0629\u060c \u0641\u0647\u0645 \u0633\u0648\u0642 \u0627\u0644\u0639\u0645\u0644\u060c \u0648\u0627\u0644\u062a\u0642\u062f\u064a\u0645 \u0639\u0644\u0649 \u0641\u0631\u0635 \u0645\u0646\u0627\u0633\u0628\u0629."
      ),
      button: tr("services.graduateJobSearch.button", "Get Started", "\u0627\u0628\u062f\u0623 \u0627\u0644\u0622\u0646"),
      icon: <BusinessCenterIcon fontSize="large" htmlColor="var(--primary-color)" />,
      image: serviceImages.graduateJobSearch,
      onClick: () => navigate("/graduate-job-search"),
    },
    {
      key: "corporateTraining",
      title: tr("services.corporateTraining.title", "Language & Cultural Training", "\u062a\u062f\u0631\u064a\u0628 \u0644\u063a\u0648\u064a \u0648\u062b\u0642\u0627\u0641\u064a (\u0644\u0644\u0634\u0631\u0643\u0627\u062a)"),
      description: tr(
        "services.corporateTraining.description",
        "Company training programs for language, communication, and German workplace culture.",
        "\u0628\u0631\u0627\u0645\u062c \u062a\u062f\u0631\u064a\u0628 \u0644\u0644\u0634\u0631\u0643\u0627\u062a \u0644\u062a\u062d\u0633\u064a\u0646 \u0627\u0644\u0644\u063a\u0629 \u0648\u0627\u0644\u062a\u0648\u0627\u0635\u0644 \u0648\u0641\u0647\u0645 \u0627\u0644\u062b\u0642\u0627\u0641\u0629 \u0627\u0644\u0645\u0647\u0646\u064a\u0629 \u0627\u0644\u0623\u0644\u0645\u0627\u0646\u064a\u0629."
      ),
      button: tr("services.corporateTraining.button", "Contact Us", "\u062a\u0648\u0627\u0635\u0644 \u0645\u0639\u0646\u0627"),
      icon: <CorporateFareIcon fontSize="large" htmlColor="var(--primary-color)" />,
      image: serviceImages.corporateTraining,
      onClick: () => navigate("/corporate-language-cultural-training"),
    },
    {
      key: "corporateRecruitment",
      title: tr("services.corporateRecruitment.title", "Employee Search for Companies", "\u0628\u062d\u062b \u0639\u0646 \u0645\u0648\u0638\u0641\u064a\u0646 (\u0644\u0644\u0634\u0631\u0643\u0627\u062a)"),
      description: tr(
        "services.corporateRecruitment.description",
        "Help companies find suitable candidates and connect them with the right roles.",
        "\u0646\u0633\u0627\u0639\u062f \u0627\u0644\u0634\u0631\u0643\u0627\u062a \u0641\u064a \u0627\u0644\u0648\u0635\u0648\u0644 \u0625\u0644\u0649 \u0645\u0631\u0634\u062d\u064a\u0646 \u0645\u0646\u0627\u0633\u0628\u064a\u0646 \u0645\u0646 \u0627\u0644\u0645\u062c\u062a\u0645\u0639 \u0627\u0644\u0639\u0631\u0628\u064a \u0648\u0631\u0628\u0637\u0647\u0645 \u0628\u0627\u0644\u0641\u0631\u0635 \u0627\u0644\u0635\u062d\u064a\u062d\u0629."
      ),
      button: tr("services.corporateRecruitment.button", "Request Service", "\u0627\u0637\u0644\u0628 \u0627\u0644\u062e\u062f\u0645\u0629"),
      icon: <GroupsIcon fontSize="large" htmlColor="var(--primary-color)" />,
      image: serviceImages.corporateRecruitment,
      onClick: () => navigate("/corporate-recruitment"),
    },
    {
      key: "events",
      title: tr3("services.events.title", "Events", "Events", "\u0627\u0644\u0641\u0639\u0627\u0644\u064a\u0627\u062a"),
      description: tr3(
        "services.events.description",
        "Join live workshops, information sessions, and community events with the ALGermanist team.",
        "Nimm an Live-Workshops, Info-Sessions und Community-Events mit dem ALGermanist Team teil.",
        "\u0627\u0646\u0636\u0645 \u0625\u0644\u0649 \u0648\u0631\u0634 \u0639\u0645\u0644 \u0648\u062c\u0644\u0633\u0627\u062a \u0645\u0628\u0627\u0634\u0631\u0629 \u0648\u0641\u0639\u0627\u0644\u064a\u0627\u062a \u0645\u0639 \u0641\u0631\u064a\u0642 ALGermanist."
      ),
      button: tr3("services.events.button", "View Events", "Events ansehen", "\u0639\u0631\u0636 \u0627\u0644\u0641\u0639\u0627\u0644\u064a\u0627\u062a"),
      icon: <EventAvailableIcon fontSize="large" htmlColor="var(--primary-color)" />,
      image: serviceImages.events,
      onClick: () => navigate("/events"),
    },
  ];

  const serviceOrder = [
    "germanLanguage",
    "ausbildung",
    "graduateJobSearch",
    "universityAdmission",
    "corporateRecruitment",
    "corporateTraining",
    "events",
  ];
  const orderedServiceItems = serviceOrder.map((key) =>
    serviceItems.find((item) => item.key === key)
  );

  return (
    <Box
      id="services"
      sx={{
        direction: isArabic ? "rtl" : "ltr",
        py: { xs: 3, sm: 4, md: 6 },
        px: { xs: 2, sm: 4, md: "100px" },
      }}
    >
      <Box textAlign={isArabic ? "right" : "left"} mb={4}>
        <Typography
          variant="h5"
          fontWeight="bold"
          mb={1}
          sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem", md: "1.75rem" } }}
        >
          {t("services.title")}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontSize: { xs: "0.95rem", sm: "1rem" } }}
        >
          {serviceSubtitle}
        </Typography>
      </Box>

      <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
        {orderedServiceItems.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} key={item.key}>
            <Box
              className={!canHover && flippedIndex === index ? "service-card is-flipped" : "service-card"}
              onClick={() => {
                if (!canHover) {
                  setFlippedIndex((current) => (current === index ? null : index));
                }
              }}
              sx={{
                perspective: "1200px",
                minHeight: { xs: 430, sm: 420, md: 400 },
                height: "100%",
                cursor: canHover ? "default" : "pointer",
                ...(canHover
                  ? {
                      "&:hover .service-card-front": {
                        opacity: 0,
                        transform: "rotateY(180deg)",
                      },
                      "&:hover .service-card-back": {
                        opacity: 1,
                        transform: "rotateY(0deg)",
                      },
                    }
                  : {}),
                "&.is-flipped .service-card-front": {
                  opacity: 0,
                  transform: "rotateY(180deg)",
                },
                "&.is-flipped .service-card-back": {
                  opacity: 1,
                  transform: "rotateY(0deg)",
                },
              }}
            >
              <Box
                className="service-card-inner"
                sx={{
                  position: "relative",
                  height: "100%",
                  minHeight: { xs: 430, sm: 420, md: 400 },
                  transformStyle: "preserve-3d",
                }}
              >
                <Box
                  className="service-card-front"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                    transform: "rotateY(0deg)",
                    opacity: 1,
                    transition: "transform 0.55s ease, opacity 0.25s ease",
                    overflow: "hidden",
                    borderRadius: 3,
                    minHeight: "100%",
                    backgroundColor: "#f8fafa",
                    border: "1px solid #E4EAEA",
                    display: "flex",
                    flexDirection: "column",
                    p: { xs: 2.5, md: 3 },
                    textAlign: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      mb: 2,
                      flexDirection: isArabic ? "row-reverse" : "row",
                    }}
                  >
                    <Box
                      sx={{
                        width: 54,
                        height: 54,
                        borderRadius: "50%",
                        bgcolor: "#fff",
                        display: "grid",
                        placeItems: "center",
                        border: "1px solid #E4EAEA",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{
                        color: "#111",
                        fontSize: { xs: "1.05rem", md: "1.2rem" },
                        lineHeight: 1.25,
                        textAlign: "center",
                      }}
                    >
                      {item.title}
                    </Typography>
                    {item.comingSoon && (
                      <Chip
                        label={t("comingSoon")}
                        size="small"
                        sx={{ bgcolor: "var(--primary-color)", color: "#111", fontWeight: 700 }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      flexGrow: 1,
                      minHeight: { xs: 190, md: 240 },
                      borderRadius: 2,
                      overflow: "hidden",
                      bgcolor: "#eef3f4",
                    }}
                  >
                    <Box
                      component="img"
                      src={item.image}
                      alt={item.title}
                      loading="lazy"
                      sx={{
                        display: "block",
                        width: "100%",
                        height: "100%",
                        minHeight: { xs: 190, md: 240 },
                        objectFit: "cover",
                        objectPosition: "center",
                        imageRendering: "auto",
                        transform: "translateZ(0)",
                      }}
                    />
                  </Box>
                </Box>

                <Box
                  className="service-card-back"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                    transform: "rotateY(-180deg)",
                    opacity: 0,
                    transition: "transform 0.55s ease, opacity 0.25s ease",
                    backgroundColor: "#f8fafa",
                    border: "1px solid #E4EAEA",
                    borderRadius: 3,
                    p: { xs: 2.5, md: 3 },
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "100%",
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 3,
                      flexGrow: 1,
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                      lineHeight: 1.75,
                      whiteSpace: "pre-line",
                      color: "#425052",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {item.description}
                  </Typography>
                  <Button
                    disabled={item.comingSoon}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!item.comingSoon) item.onClick();
                    }}
                    fullWidth
                    sx={{
                      backgroundColor: "var(--primary-color)",
                      color: "black",
                      fontWeight: "bold",
                      borderRadius: 20,
                      py: { xs: 0.9, sm: 1.05, md: 1.2 },
                      mt: "auto",
                      "&:hover": {
                        backgroundColor: "var(--primary-hover-color)",
                      },
                    }}
                  >
                    {item.comingSoon ? t("comingSoon") : item.button}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ServicesTile;
